import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import InvoicesTable from "../components/InvoicesTable";
import "../Styles/ManageInvoices.css";

const Pagination = ({ page, totalPages, onPageChange }) => {
  if (totalPages <= 1) return null;

  return (
    <div className="pagination-controls">
      <button
        className="btn"
        onClick={() => onPageChange(page - 1)}
        disabled={page <= 1}
        aria-label="Go to previous page"
      >
        &lt; {/* Changed from "&laquo; Previous" */}
      </button>
      <span>
        Page {page} of {totalPages}
      </span>
      <button
        className="btn"
        onClick={() => onPageChange(page + 1)}
        disabled={page >= totalPages}
        aria-label="Go to next page"
      >
        &gt; {/* Changed from "Next &raquo;" */}
      </button>
    </div>
  );
};

const API_BASE = process.env.REACT_APP_API_URL;

// Fallback FY calc (Apr–Mar) if /financial-years is unavailable
const getClientFY = () => {
  const d = new Date();
  const m = d.getMonth() + 1; // 1..12
  const y = d.getFullYear();
  return m >= 4 ? `${y}-${y + 1}` : `${y - 1}-${y}`;
};

const ManageInvoices = () => {
  const [invoices, setInvoices] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);

  // Financial year state (defaults populated from backend; falls back to client calc)
  const [finYear, setFinYear] = useState("");
  const [finYearOptions, setFinYearOptions] = useState([]);
  const INVOICES_PER_PAGE = 25; // Or 50, your choice
  const [page, setPage] = useState(1);
  const [totalInvoices, setTotalInvoices] = useState(0);
  const token = localStorage.getItem("token");
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  const navigate = useNavigate();
  const abortRef = useRef(null);

  // 1) Load FY options and current FY from backend (with fallback)
  useEffect(() => {
    if (!token) {
        navigate("/login");
        return;
    }

    const ac = new AbortController();

    // Fetch financial years first
    axios.get(`${API_BASE}/financial-years`, { headers, signal: ac.signal })
      .then((res) => {
        const years = res.data?.years || [];
        const current = res.data?.current || getClientFY();
        setFinYearOptions(years);
        setFinYear(current);
        // Once FY is known, fetch the first page of invoices for that FY
        fetchInvoices(searchTerm.trim(), current, page);
      })
      .catch(() => {
        const fallbackFY = getClientFY();
        setFinYearOptions([fallbackFY]);
        setFinYear(fallbackFY);
        fetchInvoices(searchTerm.trim(), fallbackFY, page);
      });

    return () => ac.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
}, []);

  // 2) Fetch invoices from backend (includes finyear param)
const fetchInvoices = async (search = "", fy = "", pageNum = 1) => {
    if (!token) {
      navigate("/login");
      return;
    }
    setLoading(true);
    try {
      if (abortRef.current) abortRef.current.abort();
      abortRef.current = new AbortController();

      const url =
        `${API_BASE}/documents?doctype=INVOICE` +
        `&search=${encodeURIComponent(search)}` +
        `&finyear=${encodeURIComponent(fy)}` +
        `&page=${pageNum}&limit=${INVOICES_PER_PAGE}`; // <-- Added page & limit

      const res = await axios.get(url, { headers, signal: abortRef.current.signal });

      // --- The next 3 lines are the key changes ---
      // Data is now pre-sorted by the backend. No more client-side sorting!
      setInvoices(res.data.items || []);
      setTotalInvoices(res.data.total || 0);

    } catch (err) {
      if (axios.isCancel(err)) return;
      console.error("Failed to load invoices", err);
      const status = err?.response?.status;
      if (status === 401) {
        localStorage.removeItem("token");
        navigate("/login");
      }
    } finally {
      setLoading(false);
    }
};

  // 3) Initial fetch (after FY is known)
  useEffect(() => {
    // This check prevents this from running on the initial render
    if (!finYear) return;

    const handler = setTimeout(() => {
        // When search term or FY changes, always reset to page 1
        const newPage = 1;
        setPage(newPage);
        fetchInvoices(searchTerm.trim(), finYear, newPage);
    }, 300); // Debounce search

    return () => clearTimeout(handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
}, [searchTerm, finYear]);

  // 4) Debounced search -> refetch with current FY
  useEffect(() => {
    if (!finYear) return;
    // Don't refetch on initial render if page is 1
    if (page === 1 && invoices.length === 0) return;

    fetchInvoices(searchTerm.trim(), finYear, page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
}, [page]);


  const handleView = (docno, fy) => {
    navigate(`/billing/view/${docno}/${fy}?doctype=INVOICE`);
  };

  const totalPages = Math.ceil(totalInvoices / INVOICES_PER_PAGE);
  const data = invoices;

  return (
    <div className="manage-invoices">
        {/* MODIFIED: Toolbar is now integrated here */}
          <div className="page-toolbar">
              <div className="toolbar-left">
                  <input
                      type="text"
                      className="search-input"
                      placeholder="Search by Invoice No or Customer"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      aria-label="Search invoices"
                  />
                  {loading && <span className="loading-badge">Loading…</span>}
              </div>

              {/* NEW: Center div for pagination */}
              <div className="toolbar-center">
                  <Pagination page={page} totalPages={totalPages} onPageChange={setPage} />
              </div>

              <div className="toolbar-right">
                  <label className="fy-filter">
                      Financial Year:
                      <select
                          className="fy-select"
                          value={finYear}
                          onChange={(e) => setFinYear(e.target.value)}
                          aria-label="Filter by financial year"
                      >
                          {finYearOptions.map(fy => <option key={fy} value={fy}>{fy}</option>)}
                      </select>
                  </label>
              </div>
          </div>

        <div className="table-wrapper">
            <InvoicesTable data={data} onView={handleView} />
            {!loading && data.length === 0 && (
            <div className="empty-state">
                {finYear ? `No invoices found for ${finYear}.` : "No invoices found."}
            </div>

            )}

        </div>
    </div>
  );
};

export default ManageInvoices;
